import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { addressApi } from "api/address";
import { formApi } from "api/form";
import RequestSubmitted from "components/common/RequestSubmitted";
import FlagHeader from "components/common/headers/FlagHeader";
import FormFooter from "components/common/footers/FormFooter";
import Layout from "components/common/layouts/Layout";
import { useApp } from "contexts/AppContext";
import { useForm } from "contexts/FormContext";
import { useModal } from "contexts/ModalContext";
import { isSoValid, parseAddressFormFieldsFromSo } from "helpers/so";
import { useContentful } from "hooks/useContentful";
import ThForm from "./ThForm";
import Loader from "components/common/loaders/Loader";

export default function ThFormContainer({ contentfulId = "", country = "" }) {
  // Context
  const {
    state: { locale, rep },
  } = useApp();
  const { form, market, overwriteAddress } = useForm();
  const { open } = useModal();

  // Custom Hooks
  const data = useContentful(contentfulId); // Contentful Id.

  // State
  const [success, setSuccess] = useState(false); // Tracks whether to render form complete.
  const [isSubmitting, setIsSubmitting] = useState(false); // Tracks when the saveMigrationData call is in progress
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isSubmitting) {
      saveMigrationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting]);

  // Scroll to error section
  const location = useLocation();
  const [scrollCount, setScrollCount] = useState(0);
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const anchor = document.getElementById(id);
      if (anchor) {
        anchor.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [scrollCount, location.hash]);

  if (!data) {
    return null;
  }

  const saveMigrationData = async () => {
    try {
      setIsLoading(true);
      const res = await formApi.saveMigrationData({
        market,
        locale,
        rep,
        form,
      });
  
      if (typeof res === "string") {
        if (res.includes("Created new user")) {
          console.log("Migration saved");
          setSuccess(true);
          return;
        } else if (res === "Contains illegal characters") {
          open({ type: "illegal-characters" });
        } else {
          open({
            type: "bank-error",
            props: { errorMessage: res },
            callback: () => {
              window.location.hash = "#bank-information";
              setScrollCount(scrollCount + 1);
            },
          });
        }
      }
      throw new Error(res); // Throw so we reenable the save button
    } catch (err) {
      console.error("Migration failed", err);
      setIsSubmitting(false);
    } finally {
      setIsLoading(false);
    }
  };

  const validateAddress = async ({ addressType, callback = () => {} }) => {
    setIsLoading(true);
    // Validate address against service objects.
    const so = await addressApi.validate({ addressType, form, country });
    setIsLoading(false);
    if (isSoValid({ so })) {
      open({
        type: "confirm-address",
        props: { addressType, form, so },
        callback,
      });
    } else {
      open({
        type: "invalid-address",
        props: {
          addressType,
          form,
          so,
          cancelCallback: () => {
            window.location.hash = "#address-section";
            setScrollCount(scrollCount + 1);
          },
        },
        callback,
      });
    }
  };

  const validateShippingOnly = () => {
    const addressType = "Ship";
    validateAddress({
      addressType,
      callback: ({ overwrite, so }) => {
        if (overwrite) {
          const address = parseAddressFormFieldsFromSo({ addressType, so });
          overwriteAddress({ address });
        }
        setIsSubmitting(true);
      },
    });
  };

  const validateShippingAndBilling = () => {
    let address = {};
    validateAddress({
      addressType: "Ship",
      callback: ({ overwrite, so }) => {
        if (overwrite) {
          address = {
            ...address,
            ...parseAddressFormFieldsFromSo({ addressType: "Ship", so }),
          };
        }
        validateAddress({
          addressType: "Bill",
          callback: ({ overwrite, so }) => {
            if (overwrite) {
              address = {
                ...address,
                ...parseAddressFormFieldsFromSo({ addressType: "Bill", so }),
              };
            }
            overwriteAddress({ address });
            setIsSubmitting(true);
          },
        });
      },
    });
  };

  const onConfirm = () => {
    if (form.AddressIsTheSame) {
      validateShippingOnly();
    } else {
      validateShippingAndBilling();
    }
  };

  const onFormSubmit = () => {
    open({
      type: "confirm-migration",
      props: { market },
      callback: onConfirm,
    });
  };

  // Contentful
  const { reference1, reference2 } = data?.fields;

  // Render if migration is successful.
  if (success) {
    return <RequestSubmitted />;
  }

  // Render migration form.
  return (
    <Layout data={data} market={market}>
      {isLoading && <Loader active />}
      <FlagHeader data={reference1} market={market} />
      <ThForm data={data} market={market} />
      <FormFooter data={reference1} onFormSubmit={onFormSubmit} isSubmitting={isSubmitting} />
    </Layout>
  );
}
